import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import type { Device } from '../../types/admin';
import type { ColumnDef } from './ColumnVisibilityMenu';

type SortField = 'DEVICE_ID' | 'SERIAL_NUMBER' | 'DATE_CODE' | 'HW_VERSION' | 'WISOL_ID' | 'LOCATION_ID' | 'GROUP';

export const DEVICE_COLUMNS: ColumnDef[] = [
  { key: 'DATE_CODE', label: 'Date Code' },
  { key: 'HW_VERSION', label: 'Hardware' },
  { key: 'WISOL_ID', label: 'Sigfox ID' },
  // Not in DEFAULT_HIDDEN on purpose: a stored hidden-set replaces the defaults
  // rather than merging with them, so a new key shows up for existing users —
  // which is what we want, since finding a RIC by site is the whole point.
  { key: 'SITE_NAME', label: 'Site Name' },
  { key: 'SIGFOX_PAC', label: 'Sigfox PAC' },
  { key: 'SIGFOX_REG', label: 'Sigfox Registered' },
  { key: 'PARTNER', label: 'Partner' },
  { key: 'CUSTOMER_STATUS', label: 'Assigned' },
  { key: 'GROUP', label: 'Group' },
  { key: 'CUSTOMER', label: 'Customer Name' },
  { key: 'CUSTOMER_EMAIL', label: 'Customer Email' },
  { key: 'NOTES', label: 'Notes' },
];

export const DEVICE_COLUMNS_DEFAULT_HIDDEN = ['DATE_CODE', 'HW_VERSION', 'SIGFOX_PAC'];

interface DeviceTableProps {
  devices: Device[];
  sortBy: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
  hiddenColumns?: Set<string>;
}

export default function DeviceTable({
  devices,
  sortBy,
  sortDir,
  onSort,
  selectedIds,
  onSelectionChange,
  hiddenColumns,
}: DeviceTableProps) {
  const navigate = useNavigate();

  if (!devices) {
    return (
      <div className="text-center py-8 text-gray-500">
        Loading devices...
      </div>
    );
  }

  const isVisible = (key: string) => !hiddenColumns?.has(key);
  const selectionEnabled = !!selectedIds && !!onSelectionChange;
  const selectedSet = new Set(selectedIds ?? []);

  const handleRowClick = (deviceId: number) => {
    navigate(`/admin/devices/${deviceId}/edit`);
  };

  const toggleOne = (deviceId: number, checked: boolean) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (checked) next.add(deviceId);
    else next.delete(deviceId);
    onSelectionChange(Array.from(next));
  };

  const allSelected = selectionEnabled && devices.length > 0
    && devices.every((d) => selectedSet.has(d.deviceId));
  const someSelected = selectionEnabled && devices.some((d) => selectedSet.has(d.deviceId)) && !allSelected;

  const toggleAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (checked) devices.forEach((d) => next.add(d.deviceId));
    else devices.forEach((d) => next.delete(d.deviceId));
    onSelectionChange(Array.from(next));
  };

  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => {
    const isActive = sortBy === field;
    const icon = !isActive ? faSort : sortDir === 'asc' ? faSortUp : faSortDown;

    return (
      <th
        className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => onSort(field)}
      >
        <div className="flex items-center gap-2">
          {label}
          <FontAwesomeIcon
            icon={icon}
            className={isActive ? 'text-blue-600' : 'text-gray-400'}
          />
        </div>
      </th>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {selectionEnabled && (
              <th className="px-4 py-2 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => { if (input) input.indeterminate = someSelected; }}
                  onChange={(e) => toggleAll(e.target.checked)}
                  aria-label="Select all visible devices"
                  className="rounded border-gray-300"
                />
              </th>
            )}
            <SortableHeader field="SERIAL_NUMBER" label="Serial #" />
            {isVisible('DATE_CODE') && <SortableHeader field="DATE_CODE" label="Date Code" />}
            {isVisible('HW_VERSION') && <SortableHeader field="HW_VERSION" label="Hardware" />}
            {isVisible('WISOL_ID') && <SortableHeader field="WISOL_ID" label="Sigfox ID" />}
            {isVisible('SITE_NAME') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                Site Name
              </th>
            )}
            {isVisible('SIGFOX_PAC') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Sigfox PAC</th>
            )}
            {isVisible('SIGFOX_REG') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Sigfox Registered</th>
            )}
            {isVisible('PARTNER') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Partner</th>
            )}
            {isVisible('CUSTOMER_STATUS') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Assigned</th>
            )}
            {isVisible('GROUP') && <SortableHeader field="GROUP" label="Group" />}
            {isVisible('CUSTOMER') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Customer Name</th>
            )}
            {isVisible('CUSTOMER_EMAIL') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Customer Email</th>
            )}
            {isVisible('NOTES') && (
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 uppercase">Notes</th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {devices.map((device) => {
            const checked = selectedSet.has(device.deviceId);
            return (
              <tr
                key={device.deviceId}
                onClick={() => handleRowClick(device.deviceId)}
                className={`cursor-pointer transition-colors duration-150 ${
                  checked ? 'bg-blue-50' : 'hover:bg-blue-50'
                }`}
              >
                {selectionEnabled && (
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleOne(device.deviceId, e.target.checked)}
                      aria-label={`Select device ${device.deviceId}`}
                      className="rounded border-gray-300"
                    />
                  </td>
                )}
                <td className="px-4 py-2 whitespace-nowrap text-blue-600 font-medium">{device.serialNumber}</td>
                {isVisible('DATE_CODE') && <td className="px-4 py-2 whitespace-nowrap">{device.dateCode}</td>}
                {isVisible('HW_VERSION') && <td className="px-4 py-2 whitespace-nowrap">{device.hardwareVersion}</td>}
                {isVisible('WISOL_ID') && <td className="px-4 py-2 whitespace-nowrap">{device.sigfoxId}</td>}
                {isVisible('SITE_NAME') && (
                  <td className="px-4 py-2 whitespace-nowrap">
                    {device.siteName ? (
                      <span className="text-gray-700">{device.siteName}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                )}
                {isVisible('SIGFOX_PAC') && <td className="px-4 py-2 whitespace-nowrap">{device.sigfoxPac}</td>}
                {isVisible('SIGFOX_REG') && (
                  <td className="px-4 py-2 whitespace-nowrap">
                    {device.sigfoxRegistered ? 'Y' : 'N'}
                  </td>
                )}
                {isVisible('PARTNER') && (
                  device.partnerName && device.partnerUserId ? (
                    <td
                      className="px-0 py-0 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/admin/users/${device.partnerUserId}/edit`)
                      }}
                    >
                      <div className="px-4 py-2 -mx-px text-blue-600 hover:bg-blue-100 hover:underline cursor-pointer">
                        {device.partnerName}
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-2 whitespace-nowrap">
                      {device.partnerName ? (
                        <span className="text-gray-700">{device.partnerName}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  )
                )}
                {isVisible('CUSTOMER_STATUS') && (
                  <td className="px-4 py-2 whitespace-nowrap">
                    {device.customerStatus === 'active' ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">Yes</span>
                    ) : device.customerStatus === 'pending' ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800">Pending</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">No</span>
                    )}
                  </td>
                )}
                {isVisible('GROUP') && (
                  device.locationName && device.locationId > 0 ? (
                    <td
                      className="px-0 py-0 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/admin/groups/${device.locationId}/edit`)
                      }}
                    >
                      <div className="px-4 py-2 -mx-px text-blue-600 hover:bg-blue-100 hover:underline cursor-pointer">
                        {device.locationName}
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-2 whitespace-nowrap text-gray-400">-</td>
                  )
                )}
                {isVisible('CUSTOMER') && (
                  device.customerStatus === 'active' && device.customerName && device.customerUserId ? (
                    <td
                      className="px-0 py-0 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/admin/users/${device.customerUserId}/edit`)
                      }}
                    >
                      <div className="px-4 py-2 -mx-px text-blue-600 hover:bg-blue-100 hover:underline cursor-pointer">
                        {device.customerName}
                      </div>
                    </td>
                  ) : (
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-gray-400">—</span>
                    </td>
                  )
                )}
                {isVisible('CUSTOMER_EMAIL') && (
                  device.customerEmail && device.customerStatus === 'active' && device.customerUserId ? (
                    <td
                      className="px-0 py-0 whitespace-nowrap"
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/admin/users/${device.customerUserId}/edit`)
                      }}
                    >
                      <div className="px-4 py-2 -mx-px text-blue-600 hover:bg-blue-100 hover:underline cursor-pointer">
                        {device.customerEmail}
                      </div>
                    </td>
                  ) : device.customerEmail ? (
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-gray-700">{device.customerEmail}</span>
                    </td>
                  ) : (
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className="text-gray-400">—</span>
                    </td>
                  )
                )}
                {isVisible('NOTES') && (
                  <td className="px-4 py-2 whitespace-nowrap truncate max-w-xs">{device.description}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
